<template>
  <UikitPageComponent
    class="osk-catalog-page"
    :error="!deeplinkData && error"
    @retry="refresh"
  >
    <BusinessProductsFiltersBlock
      ref="$filters"
      :breadcrumbs="breadcrumbs"
      :loading="pending"
      :filters="catalogFilter"
      :presets="presets"
      :sorting="(route.query.sorting as string | undefined)"
      :base-category="baseCategory"
      :page="page"
      :seo-data="seoData"
      uniq-key="catalog"
      with-routing
      need-scroll-analytics
      @refresh-deep-link="refresh"
    />

    <BusinessSeoLinks
      v-if="seoData?.linkGroups?.length"
      :links="seoData.linkGroups"
    />
  </UikitPageComponent>
</template>

<script lang="ts" setup>
import { RouteLocationNormalizedLoaded } from 'vue-router';
import { AnalyticsEvents } from '~/types/analytics';
import { TFiltersPresets } from '~/types/filters';
import {
  DeeplinkResolveResultOfobject,
} from '~/restAPI/data-contracts';

const { $addEvent } = useNuxtApp();
const schemaOrg = useSchemaOrg();

definePageMeta({
  scrollToTopFunc: (to: RouteLocationNormalizedLoaded, from: RouteLocationNormalizedLoaded) => {
    if (from.params.catalog && from.path === to.path) {
      return false;
    }

    return true;
  },
});

const $filters = ref();

const route = useRoute();

const deeplinkData = useState<DeeplinkResolveResultOfobject | null | undefined>(`catalog-page-data-${route.path}`, () => undefined);

const { getDeeplink } = useCatalog();
const { headerHeight } = useHeader();
const { getSeo, catalogFilter, seoData } = useCatalogSeo(deeplinkData);
const { t } = useI18n();
const url = useRequestURL();

const {
  error, pending, refresh,
} = useLazyAsyncData(`catalog-page-${route.fullPath}`, async () => {
  const res = await getDeeplink(route.fullPath, true);

  deeplinkData.value = res;

  if (process.server) {
    await getSeo(res);
  }

  return res;
});

const relCanonicalPath = computed(() => deeplinkData.value?.relCanonicalPath);

const brandFromDeepLink = computed(() => deeplinkData.value?.catalogFilter?.brand);

const page = computed(() => route.query.page as string);

const isDeeplinkArray = computed(() => deeplinkData.value?.object && Array.isArray(deeplinkData.value?.object));

const catalogTitle = computed(() => {
  if (isDeeplinkArray.value) {
    const items = deeplinkData.value!.object as IBreadCrumbsItem[];
    const lastEl = items[items.length - 1];

    return lastEl.fullName || lastEl.displayName || deeplinkData.value?.title;
  }

  return deeplinkData.value?.object?.title ?? deeplinkData.value?.title;
});

const breadcrumbs = computed<IBreadCrumbsItem[]>(() => {
  if (!isDeeplinkArray.value) return [{ label: catalogTitle.value }];

  const path = new Map();
  const { brand, modelDTO, modelInPath } = deeplinkData.value;

  (deeplinkData.value?.object as IBreadCrumbsItem[])
    ?.filter((item, index, self) => index === self.findIndex((selfObj) => selfObj.id === item.id)) // filter for uniq
    ?.forEach((item: IBreadCrumbsItem, index: number) => {
      if (brandFromDeepLink.value && brand && deeplinkData.value?.brandInPath) {
        const to = index === 0 ? `/catalog/${item.url}` : `/catalog/${brand.urlName}/${item.url}`;

        path.set(item.id, {
          id: item.id,
          label: item.displayName ?? '',
          to: to !== route.path ? to : undefined,
        });

        // only once based on first index
        if (index === 0) {
          path.set(brand.id, { label: brand.name, to: `/catalog/${brand.urlName}/${item.url}` });

          if (modelDTO && modelInPath) {
            path.set(modelDTO.id, { label: modelDTO.name, to: `/catalog/${brand.urlName}/${item.url}/${modelDTO.url}` });
          }
        }
      } else {
        const to = `/catalog/${item.url}`;

        path.set(item.id, {
          id: item.id,
          label: item.displayName ?? '',
          to: to !== route.path ? to : undefined,
        });
      }
    });

  const result = [...path.values()];

  result[result.length - 1].to = undefined;

  return result;
});

const baseCategory = computed(() => breadcrumbs.value[breadcrumbs.value.length - 1] ?? breadcrumbs.value[0]);

const presets = computed(() => {
  if (!deeplinkData.value?.catalogFilter?.brand || !deeplinkData.value?.brandInPath) return undefined;

  const obj: TFiltersPresets = {
    brand: deeplinkData.value?.catalogFilter?.brand,
  };

  if (deeplinkData.value?.catalogFilter?.model && deeplinkData.value?.modelInPath) {
    obj.model = deeplinkData.value?.catalogFilter?.model;
  }

  return obj;
});

const brandName = computed(() => {
  const name = deeplinkData.value?.brand?.name;

  return name ?? '';
});
const genderName = computed(() => {
  if (isDeeplinkArray.value && (deeplinkData.value!.object as IBreadCrumbsItem[]).length) {
    const name = (deeplinkData.value!.object as IBreadCrumbsItem[])[0].displayName;

    return name ?? '';
  }

  return '';
});
const categoryName = computed(() => {
  let name = deeplinkData.value?.object?.title ?? deeplinkData.value?.title;

  if (isDeeplinkArray.value) {
    const items = deeplinkData.value!.object as IBreadCrumbsItem[];
    const lastEl = items[items.length - 1];

    name = lastEl.displayName || deeplinkData.value?.title || '';
  }

  return name && name !== genderName.value ? name : '';
});

const title = computed(() => {
  if (!brandName.value && !categoryName.value) {
    return t('components.catalog.catalogPage.title1', { genderName: genderName.value });
  }

  if (brandName.value && !categoryName.value) {
    return t('components.catalog.catalogPage.title2', { genderName: genderName.value, brandName: brandName.value });
  }

  if (brandName.value) {
    return t('components.catalog.catalogPage.title3', { catalogTitle: catalogTitle.value, brandName: brandName.value, categoryName: categoryName.value });
  }

  return t('components.catalog.catalogPage.title4', { catalogTitle: catalogTitle.value, categoryName: categoryName.value });
});

const description = computed(() => {
  if (!brandName.value && !categoryName.value) {
    return t('components.catalog.catalogPage.description1', { genderName: genderName.value });
  }

  if (brandName.value && !categoryName.value) {
    return t('components.catalog.catalogPage.description2', { genderName: genderName.value, brandName: brandName.value });
  }

  if (brandName.value) {
    return t('components.catalog.catalogPage.description3', { catalogTitle: catalogTitle.value, brandName: brandName.value });
  }

  return t('components.catalog.catalogPage.description4', { catalogTitle: catalogTitle.value });
});

const headComp = computed(() => ({
  title: title.value,
  bodyAttrs: {
    class: 'oskelly-2_0',
  },
  ...(relCanonicalPath.value ? {
    link: [
      {
        rel: 'canonical',
        href: `https://oskelly.ru/${relCanonicalPath.value}`,
      },
    ],
  } : {}),
  meta: [
    {
      name: 'description',
      content: description.value,
    },
    {
      name: 'og:title',
      content: t('meta.title'),
    },
    {
      name: 'og:type',
      content: 'website',
    },
    {
      name: 'og:url',
      content: url.href + route.path,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: schemaOrg,
    },
  ],
}));

useHead(headComp);

const filtersTop = computed(() => $filters.value?.el?.offsetTop ?? 0);

onUnmounted(() => {
  deeplinkData.value = undefined;
});

watch(() => deeplinkData.value, (value) => {
  if (value) {
    $addEvent(AnalyticsEvents.PageView, {
      // eslint-disable-next-line no-param-reassign
      id_item: value.catalogFilter?.category?.reduce((result, el) => result += `${el};`, '').slice(0, -1),
    });
  }
}, { immediate: true });

watch(() => route.query, () => {
  window.scrollTo({
    top: filtersTop.value - headerHeight.value,
    behavior: 'smooth',
  });
});
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-catalog-page {
  @include media-query(lg-and-up) {
    padding-top: $indent-small;
  }
}
</style>
